import { apiClient } from './api';

export interface StaffSchedule {
  id?: string;
  staffId?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isWorking: boolean;
}

/**
 * Odstepstwo od grafiku tygodniowego na konkretna date.
 *   isOff = true                 -> tego dnia wolne
 *   isOff = false + godziny      -> pracuje, ale inaczej niz zwykle
 * Brak wpisu na date = obowiazuje grafik tygodniowy.
 */
export interface StaffScheduleException {
  id: string;
  staffId: string;
  date: string;
  isOff: boolean;
  startTime?: string | null;
  endTime?: string | null;
  reason?: string | null;
}

export interface StaffServiceLink {
  service: { id: string; name: string; duration?: number; price?: number };
}

export interface Staff {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  bio?: string;
  specialization?: string;
  commissionRate: number;
  isActive: boolean;
  userId: string;
  salonId: string;
  user?: { id: string; email: string };
  salon?: { id: string; name: string };
  services?: StaffServiceLink[];
  schedules?: StaffSchedule[];
  _count?: { bookings: number };
}

interface AddStaffData {
  salonId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  bio?: string;
  specialization?: string;
  commissionRate?: number;
}

interface UpdateStaffData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  bio?: string;
  specialization?: string;
  commissionRate?: number;
  isActive?: boolean;
}

export const staffApi = {
  async listBySalon(salonId: string, includeInactive = false) {
    const res = await apiClient.get<Staff[]>(`/staff/salon/${salonId}`, {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return res.data;
  },

  async getById(id: string) {
    const res = await apiClient.get<Staff>(`/staff/${id}`);
    return res.data;
  },

  async add(data: AddStaffData) {
    const res = await apiClient.post<Staff>('/staff', data);
    return res.data;
  },

  async update(id: string, data: UpdateStaffData) {
    const res = await apiClient.put<Staff>(`/staff/${id}`, data);
    return res.data;
  },

  async remove(id: string) {
    const res = await apiClient.delete(`/staff/${id}`);
    return res.data;
  },

  async getSchedule(staffId: string) {
    const res = await apiClient.get<StaffSchedule[]>(`/staff/${staffId}/schedule`);
    return res.data;
  },

  async setSchedule(staffId: string, schedules: Omit<StaffSchedule, 'id' | 'staffId'>[]) {
    const res = await apiClient.put(`/staff/${staffId}/schedule`, { schedules });
    return res.data;
  },

  async getScheduleExceptions(staffId: string, params?: { from?: string; to?: string }) {
    const res = await apiClient.get<StaffScheduleException[]>(
      `/staff/${staffId}/schedule-exceptions`,
      { params },
    );
    return res.data;
  },

  async upsertScheduleException(
    staffId: string,
    data: { date: string; isOff: boolean; startTime?: string; endTime?: string; reason?: string },
  ) {
    const res = await apiClient.put<StaffScheduleException>(
      `/staff/${staffId}/schedule-exceptions`,
      data,
    );
    return res.data;
  },

  async deleteScheduleException(staffId: string, exceptionId: string) {
    const res = await apiClient.delete(`/staff/${staffId}/schedule-exceptions/${exceptionId}`);
    return res.data;
  },

  async assignServices(staffId: string, serviceIds: string[]) {
    const res = await apiClient.put<Staff>(`/staff/${staffId}/services`, { serviceIds });
    return res.data;
  },
};
